<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Button from 'dashboard/components-next/button/Button.vue';
import CartSelector from './CartSelector.vue';
import SidebarActionsHeader from 'dashboard/components-next/SidebarActionsHeader.vue';
import CartItem from './CartItem.vue';
import CatalogProductPopover from 'catalog/components/CatalogProductPopover.vue';
import ShippingOptionsPopover from './ShippingOptionsPopover.vue';
import CartAPI from 'cart/api/cart';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  contactId: { type: [Number, String], required: true },
});

const { t } = useI18n();
const { localeTag } = useDateLocale();
const store = useStore();
const { updateUISettings } = useUISettings();

const showProductPopover = ref(false);
const couponCode = ref('');
const applyingDiscount = ref(false);
const cepCode = ref('');
const calculatingShipping = ref(false);
const shippingOptions = ref([]);
const showShippingPopover = ref(false);
const addingProduct = ref(false);
const loadingItemId = ref(null);
const removingDiscount = ref(false);
const creatingNewCart = ref(false);
const sendingCheckout = ref(false);
const allCarts = ref([]);
const selectedCartId = ref('');

const cart = computed(() =>
  store.getters['cart/getCartByConversation'](props.conversationId)
);
const isLoading = computed(() =>
  store.getters['cart/isLoading'](props.conversationId)
);

const currency = computed(() => cart.value?.currency || 'BRL');
const items = computed(() => cart.value?.items || []);
const hasItems = computed(() => items.value.length > 0);
const cartStatus = computed(() => cart.value?.status || 'open');
const isActive = computed(
  () => !!cart.value && ['open', 'abandoned'].includes(cartStatus.value)
);
const isReadOnly = computed(() => cartStatus.value !== 'open');
const isExpired = computed(
  () => cart.value?.expires_at && new Date(cart.value.expires_at) < new Date()
);

const cartOptions = computed(() => {
  const opts = allCarts.value.map(c => ({
    value: c.id.toString(),
    cartId: c.id,
    status: c.status,
    statusLabel: t(`CONVERSATION.CART.STATUS.${c.status}`),
    label: `#${c.id}`,
  }));
  opts.push({
    value: 'new',
    label: t('CONVERSATION.CART.NEW_CART'),
    status: null,
  });
  return opts;
});

watch(
  () => cart.value?.id,
  id => {
    if (id) selectedCartId.value = id.toString();
  },
  { immediate: true }
);

watch(
  () => cart.value?.discount_code,
  code => {
    couponCode.value = code || '';
  },
  { immediate: true }
);

const discountAmount = computed(() => {
  const sub = parseFloat(cart.value?.subtotal_amount);
  const total = parseFloat(cart.value?.total_amount);
  if (!sub || !total || sub <= total) return null;
  return sub - total;
});

const formatPrice = amount =>
  amount != null
    ? new Intl.NumberFormat(localeTag.value, {
        style: 'currency',
        currency: currency.value,
      }).format(amount)
    : '—';

const closePanel = () => updateUISettings({ is_cart_panel_open: false });

const fetchCart = () =>
  store.dispatch('cart/fetchByContact', {
    conversationId: props.conversationId,
    contactId: props.contactId,
  });

const fetchCartIfEmpty = () => {
  if (!cart.value) fetchCart();
};

const fetchAllCarts = async () => {
  try {
    const { data } = await CartAPI.getContactCarts(props.contactId);
    allCarts.value = data.carts || [];
  } catch (_e) {
    // non-critical
  }
};

watch(() => props.conversationId, fetchCart, { immediate: false });
watch(() => props.contactId, fetchAllCarts, { immediate: false });

const onMessageSent = () => {
  if (cartStatus.value === 'open') fetchCart();
};

onMounted(() => {
  fetchCartIfEmpty();
  fetchAllCarts();
  emitter.on(BUS_EVENTS.MESSAGE_SENT, onMessageSent);
});
onUnmounted(() => {
  emitter.off(BUS_EVENTS.MESSAGE_SENT, onMessageSent);
});

const alertCartError = e => {
  const key = e?.response?.data?.error_key;
  useAlert(t(`CONVERSATION.CART.ERRORS.${key ?? 'GENERIC'}`));
};

const onUpdateQty = async ({ item, quantity }) => {
  if (!cart.value) return;
  loadingItemId.value = item.id;
  try {
    await store.dispatch('cart/updateItem', {
      conversationId: props.conversationId,
      cartId: cart.value.id,
      itemId: item.id,
      quantity,
    });
  } catch (e) {
    alertCartError(e);
  } finally {
    loadingItemId.value = null;
  }
};

const onRemoveItem = async item => {
  if (!cart.value) return;
  try {
    await store.dispatch('cart/removeItem', {
      conversationId: props.conversationId,
      cartId: cart.value.id,
      itemId: item.id,
    });
  } catch (e) {
    alertCartError(e);
  }
};

const onProductSelect = async variant => {
  showProductPopover.value = false;
  addingProduct.value = true;
  try {
    await store.dispatch('cart/addItem', {
      conversationId: props.conversationId,
      cartId: cart.value.id,
      variantId: variant.localId,
      quantity: 1,
    });
  } catch (e) {
    alertCartError(e);
  } finally {
    addingProduct.value = false;
  }
};

const onApplyDiscount = async () => {
  if (!couponCode.value.trim() || !cart.value) return;
  applyingDiscount.value = true;
  try {
    await store.dispatch('cart/applyDiscount', {
      conversationId: props.conversationId,
      cartId: cart.value.id,
      code: couponCode.value.trim(),
    });
  } catch (e) {
    const key = e?.response?.data?.error_key;
    useAlert(t(`CONVERSATION.CART.ERRORS.${key ?? 'COUPON_INVALID'}`));
  } finally {
    applyingDiscount.value = false;
  }
};

const onRemoveDiscount = async () => {
  if (!cart.value) return;
  removingDiscount.value = true;
  try {
    await store.dispatch('cart/removeDiscount', {
      conversationId: props.conversationId,
      cartId: cart.value.id,
    });
  } catch (e) {
    alertCartError(e);
  } finally {
    removingDiscount.value = false;
  }
};

const onCalculateShipping = async () => {
  if (!cepCode.value.trim() || !cart.value) return;
  calculatingShipping.value = true;
  showShippingPopover.value = false;
  try {
    shippingOptions.value = await store.dispatch('cart/shippingQuote', {
      cartId: cart.value.id,
      recipientCep: cepCode.value.trim(),
    });
    showShippingPopover.value = true;
  } catch (e) {
    alertCartError(e);
  } finally {
    calculatingShipping.value = false;
  }
};

const onSelectShipping = async option => {
  showShippingPopover.value = false;
  try {
    await store.dispatch('cart/applyShipping', {
      conversationId: props.conversationId,
      cartId: cart.value.id,
      serviceCode: option.service_code,
      name: option.name,
      price: option.price,
      deliveryTime: option.delivery_time,
    });
  } catch (e) {
    alertCartError(e);
  }
};

const onSendCheckout = async () => {
  if (!cart.value?.checkout_url) return;
  sendingCheckout.value = true;
  try {
    await store.dispatch('cart/sendCheckoutLink', {
      conversationId: props.conversationId,
      cartId: cart.value.id,
    });
  } catch (e) {
    alertCartError(e);
  } finally {
    sendingCheckout.value = false;
  }
};

const onNewCart = async () => {
  creatingNewCart.value = true;
  try {
    await store.dispatch('cart/newCart', {
      conversationId: props.conversationId,
      contactId: props.contactId,
    });
    await fetchAllCarts();
  } catch (e) {
    alertCartError(e);
  } finally {
    creatingNewCart.value = false;
  }
};

const onSelectCart = async value => {
  if (value === 'new') {
    await onNewCart();
    return;
  }
  const found = allCarts.value.find(c => c.id.toString() === value);
  if (found) {
    store.dispatch('cart/setCart', {
      conversationId: props.conversationId,
      cart: found,
    });
  }
};
</script>

<template>
  <div class="flex flex-col h-full text-sm leading-6 tracking-tight w-full">
    <SidebarActionsHeader
      :title="t('CONVERSATION.CART.TITLE')"
      @close="closePanel"
    >
      <template #badge>
        <CartSelector
          :options="cartOptions"
          :model-value="selectedCartId"
          @update:model-value="onSelectCart"
        />
      </template>
    </SidebarActionsHeader>

    <div v-if="isLoading" class="flex-1 flex items-center justify-center">
      <i class="i-lucide-loader-2 animate-spin text-n-slate-9 text-2xl" />
    </div>

    <!-- Estado: carrinho inativo (convertido) -->
    <div
      v-else-if="!isActive"
      class="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center"
    >
      <i
        :class="
          cartStatus === 'converted'
            ? 'i-lucide-circle-check-big text-n-blue-9'
            : 'i-lucide-clock text-n-slate-8'
        "
        class="text-4xl"
      />
      <div>
        <p class="text-sm font-medium text-n-slate-12 mb-1">
          {{
            t(`CONVERSATION.CART.INACTIVE.${cartStatus.toUpperCase()}_TITLE`)
          }}
        </p>
        <p class="text-xs text-n-slate-10">
          {{ t(`CONVERSATION.CART.INACTIVE.${cartStatus.toUpperCase()}_DESC`) }}
        </p>
      </div>
      <Button
        ghost
        slate
        sm
        icon="i-lucide-shopping-cart"
        :label="t('CONVERSATION.CART.NEW_CART')"
        :is-loading="creatingNewCart"
        @click="onNewCart"
      />
    </div>

    <template v-else>
      <!-- Item list -->
      <div
        class="flex-1 overflow-y-auto overflow-x-hidden px-4 scrollbar-custom"
      >
        <div v-if="hasItems">
          <CartItem
            v-for="item in items"
            :key="item.id"
            :item="item"
            :currency="currency"
            :loading="loadingItemId === item.id"
            :readonly="isReadOnly"
            @update-qty="onUpdateQty"
            @remove="onRemoveItem"
          />
        </div>
        <div
          v-else
          class="flex flex-col items-center justify-center py-12 text-center"
        >
          <i class="i-lucide-shopping-cart text-3xl text-n-slate-8 mb-3" />
          <p class="text-sm text-n-slate-11">
            {{ t('CONVERSATION.CART.EMPTY') }}
          </p>
        </div>
      </div>

      <!-- Add products button (open carts only) -->
      <div v-if="!isReadOnly" class="px-4 pt-2 relative">
        <Button
          ghost
          slate
          sm
          icon="i-lucide-plus"
          :label="t('CONVERSATION.CART.ADD_PRODUCTS')"
          :is-loading="addingProduct"
          :disabled="addingProduct"
          class="w-full justify-center"
          @click="showProductPopover = !showProductPopover"
        />
        <CatalogProductPopover
          v-if="showProductPopover"
          anchored
          :currency="currency"
          @select="onProductSelect"
          @close="showProductPopover = false"
        />
      </div>

      <!-- Coupon + CEP (open carts only) -->
      <div v-if="!isReadOnly" class="px-4 pt-2 space-y-1.5">
        <div
          class="flex items-center gap-1.5 px-2.5 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus-within:outline-n-brand dark:focus-within:outline-n-brand"
        >
          <input
            v-model="couponCode"
            type="text"
            :disabled="!!cart?.discount_code"
            class="reset-base flex-1 min-w-0 h-8 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-10 disabled:opacity-60 disabled:cursor-default"
            :placeholder="t('CONVERSATION.CART.DISCOUNT_PLACEHOLDER')"
            @keyup.enter="onApplyDiscount"
          />
          <Button
            v-if="cart?.discount_code"
            faded
            ruby
            xs
            :label="t('CONVERSATION.CART.REMOVE_DISCOUNT')"
            :is-loading="removingDiscount"
            :hide-label="removingDiscount"
            :disabled="removingDiscount"
            class="shrink-0"
            @click="onRemoveDiscount"
          />
          <Button
            v-else
            faded
            blue
            xs
            :label="t('CONVERSATION.CART.APPLY_DISCOUNT')"
            :is-loading="applyingDiscount"
            :hide-label="applyingDiscount"
            :disabled="!couponCode.trim() || applyingDiscount"
            class="shrink-0"
            @click="onApplyDiscount"
          />
        </div>
        <div
          class="relative flex items-center gap-1.5 px-2.5 rounded-lg bg-n-alpha-black2 outline outline-1 outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 focus-within:outline-n-brand dark:focus-within:outline-n-brand"
        >
          <input
            v-model="cepCode"
            type="text"
            maxlength="9"
            class="reset-base flex-1 min-w-0 h-8 bg-transparent !text-xs !outline-0 text-n-slate-12 placeholder:text-n-slate-10"
            :placeholder="t('CONVERSATION.CART.CEP_PLACEHOLDER')"
            @keyup.enter="onCalculateShipping"
          />
          <Button
            faded
            blue
            xs
            :label="t('CONVERSATION.CART.CALCULATE_SHIPPING')"
            :is-loading="calculatingShipping"
            :hide-label="calculatingShipping"
            :disabled="!cepCode.trim() || calculatingShipping"
            class="shrink-0"
            @click="onCalculateShipping"
          />
          <ShippingOptionsPopover
            v-if="showShippingPopover"
            :options="shippingOptions"
            :currency="currency"
            @select="onSelectShipping"
            @close="showShippingPopover = false"
          />
        </div>
      </div>

      <!-- Totals + footer -->
      <div class="px-4 py-4 border-t border-n-weak mt-2 space-y-1.5">
        <div class="flex justify-between text-xs text-n-slate-11">
          <span>{{ t('CONVERSATION.CART.SUBTOTAL') }}</span>
          <span>{{ formatPrice(cart?.subtotal_amount) }}</span>
        </div>
        <div
          v-if="cart?.shipping_amount != null"
          class="flex justify-between text-xs text-n-slate-11"
        >
          <span
            >{{ t('CONVERSATION.CART.SHIPPING')
            }}<span
              v-if="cart.shipping_service_name"
              class="text-n-slate-9 ml-1"
              >· {{ cart.shipping_service_name }}</span
            ></span
          >
          <span>{{ formatPrice(cart.shipping_amount) }}</span>
        </div>
        <div
          v-if="discountAmount"
          class="flex justify-between text-xs text-n-teal-11"
        >
          <span>{{ t('CONVERSATION.CART.DISCOUNT') }}</span>
          <span>- {{ formatPrice(discountAmount) }}</span>
        </div>
        <div class="flex justify-between text-sm font-semibold text-n-slate-12">
          <span>{{ t('CONVERSATION.CART.TOTAL') }}</span>
          <span>{{ formatPrice(cart?.total_amount) }}</span>
        </div>

        <div class="pt-1">
          <Button
            blue
            sm
            icon="i-lucide-shopping-cart"
            :label="t('CONVERSATION.CART.SEND_CHECKOUT')"
            :disabled="!cart?.checkout_url || sendingCheckout || isExpired"
            :is-loading="sendingCheckout"
            class="w-full justify-center"
            @click="onSendCheckout"
          />
        </div>
      </div>
    </template>
  </div>
</template>
